import { useEffect, useRef, useMemo } from 'react';
import {
  generatePath,
  Outlet,
  useParams,
  NavLink,
  useLocation,
  useOutletContext,
} from 'react-router-dom';
import queryString from 'query-string';
import {
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  Filters,
  useFilters,
  IconExpand,
  SchemaFilterType,
  PermissionEnum,
  useGetList,
  usePrevious,
} from '@ui/src';
import { useAppSelector } from '@store';
import { useLazyGetExchangeRatesQuery } from './store/services';
import {
  rateListColumns,
  rateListSchema,
  rateListFiltersSchema,
  rateListColumnsLabels,
  rateListAllColumnsLabels,
  rateListAllFiltersSchema,
  rateListAllSchema,
} from './variables/list';
import { RateItem } from './types';
import formatRateList from './helpers/format-rates';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const RateList = () => {
  const { date } = useOutletContext<{ date: string }>();

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const {
    rates: {
      list: { rates, pagination },
    },
    exchangeDictionaries: { currencies, rateSource, activePermissions },
  } = useAppSelector((state) => state);

  const { currency } = useParams();

  const { filters, sort, onFilterChange } = useQueryState(
    currency === 'all' ? rateListAllSchema : rateListSchema,
  );

  const [getList, { isLoading, isFetching }] = useLazyGetExchangeRatesQuery();

  const { search } = useLocation();

  const prevCurrency = usePrevious(currency);
  const prevDate = usePrevious(date);

  const formattedData = useMemo(() => {
    const data = formatRateList(
      rates as RateItem[],
      currency as string,
      rateSource,
    );

    return data;
  }, [rates, sort, filters, rateSource]);

  const { getListAction, onFilter, setPage, errorList } = useGetList<RateItem>({
    getList,
    onFilterChange,
    onSortChange: () => {},
    schema: currency === 'all' ? rateListAllSchema : rateListSchema,
  });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema:
      currency === 'all' ? rateListAllFiltersSchema : rateListFiltersSchema,
    filters,
    onFilterChange: () => {},
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (filters && !Object.keys(filters)?.length && !search && !rates) {
      let currentFilters = filters;

      if (currency !== 'all') {
        currentFilters = {
          ...currentFilters,
          currencyLeftId: {
            value: [currency as string],
            type: SchemaFilterType.STRING,
          },
        };
      }

      if (!filters.date) {
        const filtersValues: Filters = {
          ...currentFilters,
        };

        filtersValues.date = {
          value: [date],
          type: SchemaFilterType.STRING,
        };

        onFilter({
          ...filtersValues,
        });
      }
    }
  }, [filters, rates, search]);

  useEffect(() => {
    if (filters && Object.keys(filters)?.length && !rates && search.length) {
      let currentFilters = filters;

      if (currency !== 'all') {
        currentFilters = {
          ...currentFilters,
          currencyLeftId: {
            value: [currency as string],
            type: SchemaFilterType.STRING,
          },
        };
      }

      if (!filters.date) {
        const filtersValues: Filters = {
          ...currentFilters,
        };

        filtersValues.date = {
          value: [date],
          type: SchemaFilterType.STRING,
        };

        onFilter({
          ...filtersValues,
        });
      } else if (!rates) {
        getListAction({
          filters: {
            ...currentFilters,
          },
        });
      }
    } else if (
      (prevCurrency && currency !== prevCurrency) ||
      (prevDate && prevDate !== date)
    ) {
      const nextFilters: Filters = {};

      if (currency !== 'all') {
        nextFilters.currencyLeftId = {
          value: [currency as string],
          type: SchemaFilterType.STRING,
        };
      }

      if (search) {
        const parsed = queryString.parse(search.replace('?', ''));

        if (Object.keys(parsed).length) {
          Object.keys(parsed).forEach((item) => {
            nextFilters[item] = {
              value: [parsed[item] as string],
              type: SchemaFilterType.STRING,
            };
          });
        }

        nextFilters.date = {
          value: [date],
          type: SchemaFilterType.STRING,
        };
      }

      onFilter(nextFilters);
    }
  }, [filters, sort, rates, currency, prevCurrency, date, prevDate, search]);

  useEffect(() => {
    if (currencies) {
      let formattedCurrencies = currencies.map((item) => ({
        name: `${item.name} (${item.description})`,
        value: item.id,
      }));

      if (currency !== 'all') {
        formattedCurrencies = formattedCurrencies.filter(
          (item) => item.value !== currency,
        );
      }
      setValues('currencyRightId', formattedCurrencies);
      setValues('currencyLeftId', formattedCurrencies);
    }

    if (rateSource) {
      setValues(
        'rateSourceId',
        rateSource.map((item) => ({ name: item.name, value: item.id })),
      );
    }
  }, [currencies, currency, rateSource]);

  useEffect(() => {
    let title = 'Exchange rates';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  const addUrl = generatePath(ROUTES.RATES_ADD, {
    currency: currency as string,
  });

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.ratesLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={rateListColumns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        sort={sort}
        columnsLabels={
          currency === 'all' ? rateListAllColumnsLabels : rateListColumnsLabels
        }
        onFilterChange={(filtersValue) => {
          const nextFilters = { ...filtersValue };

          if (currency !== 'all') {
            nextFilters.currencyLeftId = {
              value: [currency as string],
              type: SchemaFilterType.STRING,
            };
          }

          if (date) {
            nextFilters.date = {
              value: [date],
              type: SchemaFilterType.STRING,
            };
          }

          onFilter(nextFilters);
        }}
        error={errorList}
        filtersComponent={filtersComponent}
        isLoading={isFetching}
        tableBodyClassName={`${!rates?.length ? cn.emptyTable : ''} ${
          isFetching ? cn.tableLoading : ''
        }`}
        tableTheadClassName={cn.rateTableHead}
        handleSubmit={handleSubmit}
        pagination={pagination}
        take="20"
        setPage={(data) => {
          const nextFilters = {
            ...filters,
          };

          if (currency !== 'all') {
            nextFilters.currencyLeftId = {
              value: [currency as string],
              type: SchemaFilterType.STRING,
            };
          }

          setPage({ ...data, filters: nextFilters });
        }}
        topContent={
          <div className={cn.ratesTop}>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_EXCHANGE_EDIT_PAIRS_ACTION,
            ) && (
              <NavLink to={`${addUrl}${search || ''}`} className={cn.addRate}>
                <span>
                  <IconExpand />
                </span>
                <span>Add Rate</span>
              </NavLink>
            )}
          </div>
        }
        clickable
      />
    );
  }

  return (
    <div className={cn.ratesWrapper} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default RateList;
